import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import NotificationPanel from './NotificationPanel';
import EmailComposer from './EmailComposer';
import SafeIcon from '../common/SafeIcon';
import { TAB_SECTIONS } from '../config/navigation';
import { FiBell, FiLogOut, FiHome, FiMail } from 'react-icons/fi';
import { differenceInDays, parseISO } from 'date-fns';

const Layout = () => {
  const { data } = useStore();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);

  const expiringCount = data.properties.filter(p => {
    if (!p.leaseEnd) return false;
    const daysLeft = differenceInDays(parseISO(p.leaseEnd), new Date());
    return daysLeft <= 30;
  }).length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTabActive = (tabPath) => {
    if (tabPath === '/') return location.pathname === '/';
    return location.pathname.startsWith(tabPath);
  };

  const userInitials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : 'U';

  return (
    <div className="flex flex-col h-screen bg-gray-50 font-sans">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 z-30 shrink-0">
        <div className="flex items-center justify-between h-14 px-4 lg:px-6">
          {/* Logo */}
          <div className="flex items-center gap-2 shrink-0">
            <img src="/logo.png" onError={e => { e.target.onerror = null; e.target.src = '/logo.svg'; }} alt="PropManagePro" className="w-8 h-8" />
            <span className="text-sm font-bold text-gray-900 hidden sm:block">PropManagePro</span>
          </div>

          {/* Tab Navigation */}
          <nav className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide mx-4">
            {TAB_SECTIONS.map(({ key, label, path, icon }) => (
              <NavLink
                key={key}
                to={path}
                end={path === '/'}
                className={() =>
                  `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isTabActive(path)
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`
                }
              >
                <SafeIcon icon={icon} size={15} />
                <span className="hidden md:inline">{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Notifications */}
            {/* Email Compose */}
            <button
              onClick={() => setShowEmailComposer(true)}
              className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title="Compose Email"
            >
              <FiMail size={18} />
            </button>

            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`p-2 rounded-lg transition-colors ${
                  showNotifications
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FiBell size={18} />
                {expiringCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white ring-2 ring-white">
                    {expiringCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <NotificationPanel
                  properties={data.properties}
                  onClose={() => setShowNotifications(false)}
                />
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                  {userInitials}
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Property Manager</p>
                  </div>
                  <button
                    onClick={signOut}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <FiLogOut size={15} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>

      <EmailComposer isOpen={showEmailComposer} onClose={() => setShowEmailComposer(false)} />
    </div>
  );
};

export default Layout;
